import { useNavigate, useLocation, Link } from 'react-router-dom'
import { LayoutGrid, FileText, Cog, Wrench, BarChart3, Shield, LogOut } from 'lucide-react'
import { APP_VERSION } from '../../version'

export const SIDEBAR_WIDTH = 220

interface SidebarProps {
  isAdmin?: boolean
  onSignOut?: () => void
}

const navItems = [
  { to: '/dashboard', icon: LayoutGrid, label: 'Dashboard', exact: true },
  { to: '/dashboard/projects', icon: FileText, label: 'Projects', exact: false },
  { to: '/dashboard/machines', icon: Cog, label: 'Machines', exact: false },
  { to: '/dashboard/tools', icon: Wrench, label: 'Tools', exact: false },
  { to: '/dashboard/usage', icon: BarChart3, label: 'Usage', exact: false },
] as const

export default function Sidebar({ isAdmin, onSignOut }: SidebarProps) {
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const isActive = (to: string, exact?: boolean) =>
    exact ? pathname === to : pathname.startsWith(to)

  return (
    <nav
      className="fixed top-0 left-0 z-50 flex flex-col border-r border-cyan-500/20 bg-[#030712]"
      style={{ width: SIDEBAR_WIDTH, height: '100vh' }}
    >
      {/* Logo */}
      <div
        className="flex items-center gap-2.5 px-5 py-5 cursor-pointer"
        onClick={() => navigate('/dashboard')}
      >
        <svg width="24" height="19" viewBox="0 0 32 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <clipPath id="mtn-sb">
              <path d="M2 27 C5 27 7 22 9 18 C11 14 13 10 16 7 C19 10 21 14 23 18 C25 22 27 27 30 27 Z"/>
            </clipPath>
            <linearGradient id="g-sb" x1="2" y1="7" x2="30" y2="27" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#06b6d4"/>
              <stop offset="100%" stopColor="#3b82f6"/>
            </linearGradient>
          </defs>
          <g clipPath="url(#mtn-sb)">
            <rect x="0" y="7"    width="32" height="2"   fill="url(#g-sb)"/>
            <rect x="0" y="10.5" width="32" height="2"   fill="url(#g-sb)"/>
            <rect x="0" y="14"   width="32" height="2"   fill="url(#g-sb)"/>
            <rect x="0" y="17.5" width="32" height="2"   fill="url(#g-sb)"/>
            <rect x="0" y="21"   width="32" height="2"   fill="url(#g-sb)"/>
            <rect x="0" y="24.5" width="32" height="3"   fill="url(#g-sb)"/>
          </g>
        </svg>
        <span
          className="select-none tracking-widest uppercase"
          style={{
            fontFamily: "'Barlow', sans-serif",
            fontWeight: 900,
            fontSize: '1rem',
            background: 'linear-gradient(135deg, #06b6d4, #3b82f6)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            filter: 'drop-shadow(0 0 8px rgba(6,182,212,0.5))',
          }}
        >
          GCODR
        </span>
        <span className="font-mono text-[10px] text-slate-600">v{APP_VERSION}</span>
      </div>

      {/* Sign out — above nav */}
      {onSignOut && (
        <div className="px-3 mt-1 mb-2">
          <button
            onClick={onSignOut}
            className="flex items-center gap-3 px-3 py-2 rounded-lg font-mono text-xs text-slate-600 hover:text-slate-400 hover:bg-white/5 transition-all duration-150 w-full"
          >
            <LogOut className="w-4 h-4 shrink-0" />
            Sign out
          </button>
        </div>
      )}

      {/* Nav items */}
      <div data-tour="sidebar" className="flex flex-col gap-0.5 px-3">
        {navItems.map(({ to, icon: Icon, label, exact }) => {
          const active = isActive(to, exact)
          return (
            <button
              key={to}
              onClick={() => navigate(to)}
              data-tour={to === '/dashboard/machines' ? 'machines' : to === '/dashboard/tools' ? 'tools' : undefined}
              className={`
                flex items-center gap-3 px-3 py-2 rounded-lg font-mono text-xs transition-all duration-150
                ${active
                  ? 'bg-cyan-500/10 text-cyan-400 border-l-2 border-cyan-400'
                  : 'text-slate-500 hover:text-slate-300 hover:bg-white/5 border-l-2 border-transparent'
                }
              `}
            >
              <Icon className="w-4 h-4 shrink-0" />
              {label}
            </button>
          )
        })}

        {/* Admin link */}
        {isAdmin && (
          <button
            onClick={() => navigate('/admin')}
            className={`
              flex items-center gap-3 px-3 py-2 rounded-lg font-mono text-xs transition-all duration-150 mt-2
              ${pathname.startsWith('/admin')
                ? 'bg-cyan-500/10 text-cyan-400 border-l-2 border-cyan-400'
                : 'text-slate-500 hover:text-slate-300 hover:bg-white/5 border-l-2 border-transparent'
              }
            `}
          >
            <Shield className="w-4 h-4 shrink-0" />
            Admin
          </button>
        )}

      </div>

      {/* Legal links — bottom of sidebar */}
      <div className="mt-auto px-4 pb-4 space-y-2">
        <div className="border-t border-slate-800/40 pt-3 space-y-1.5">
          <Link to="/terms" className="block font-mono text-xs text-slate-600 hover:text-slate-400 transition-colors">
            Terms of Service
          </Link>
          <Link to="/privacy" className="block font-mono text-xs text-slate-600 hover:text-slate-400 transition-colors">
            Privacy Policy
          </Link>
          <Link to="/report-bug" className="block font-mono text-xs text-slate-600 hover:text-slate-400 transition-colors">
            Report a Bug
          </Link>
        </div>
        <p className="font-mono text-[10px] text-slate-700">
          <a href="mailto:support@gcodr.dev" className="text-cyan-500/40 hover:text-cyan-400 transition-colors">
            support@gcodr.dev
          </a>
        </p>
      </div>
    </nav>
  )
}
